import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/contact', label: 'Contact' }
  ];

  const scrollToContact = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      window.location.href = '/#contact';
    } else {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-zinc-900 text-gray-300 py-2 text-sm border-b border-zinc-800">
        <div className="container mx-auto px-4 flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-4">
            <a href={`tel:${businessInfo.phone}`} className="flex items-center gap-2 hover:text-orange-500 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">{businessInfo.phone}</span>
            </a>
            <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-2 hover:text-orange-500 transition-colors">
              <Mail className="w-4 h-4" />
              <span className="hidden md:inline">{businessInfo.email}</span>
            </a>
          </div>
          <div className="text-xs sm:text-sm">
            Servicing {businessInfo.serviceArea}
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-zinc-900/95 backdrop-blur-md shadow-lg'
            : 'bg-zinc-900'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="bg-white rounded-lg p-2 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-orange-500/30">
                <img 
                  src="https://customer-assets.emergentagent.com/job_adelaide-tipper-hire/artifacts/mery91eo_Af991094c974f4b6c8993f17f1aee4d36N.png"
                  alt="Adelaide Hills Tipper Hire Logo"
                  className="h-12 w-auto"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors relative group ${
                    location.pathname === link.path
                      ? 'text-orange-500'
                      : 'text-gray-300 hover:text-orange-500'
                  }`}
                >
                  {link.label}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-orange-500 transition-all duration-300 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Button
                onClick={scrollToContact}
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 shadow-lg hover:shadow-orange-500/50 transition-all duration-300"
              >
                Get a Quote
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-zinc-800 border-t border-zinc-700">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`py-3 px-4 rounded-lg font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-300 hover:bg-zinc-700'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Button
                onClick={scrollToContact}
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold mt-2"
              >
                Get a Quote
              </Button>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
